import React, { useEffect } from "react";
import { X, Car, ShieldCheck } from "lucide-react";
import RideBookingForm from "./RideBookingForm";

const RideBookingModal = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = e => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-[#061A3A]/65 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ride-booking-title"
    >
      {/* Background */}
      <button
        type="button"
        aria-label="Close booking modal"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
      />

      {/* Modal */}
      <div
        className="
          relative z-10 flex w-full max-w-3xl flex-col overflow-hidden
          rounded-t-[28px] bg-white shadow-2xl
          animate-[modalUp_.3s_ease-out]
          max-h-[94vh]
          sm:rounded-[28px]
          sm:max-h-[92vh]
        "
        onClick={e => e.stopPropagation()}
      >
        {/* Top Header */}
        <div className="shrink-0 border-b border-[#E2E8E5] bg-white">
          <div className="flex items-center justify-between px-5 py-4 sm:px-6 sm:py-5">
            <div className="flex min-w-0 items-center gap-3">
              {/* Icon */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#00A260]/10 text-[#00A260]">
                <Car className="h-5 w-5" />
              </div>

              {/* Title */}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2
                    id="ride-booking-title"
                    className="truncate text-base font-extrabold text-[#061A3A] sm:text-lg"
                  >
                    Book Your Ride
                  </h2>

                  <span className="hidden rounded-full bg-[#00A260]/10 px-2 py-0.5 text-[9px] font-bold text-[#00A260] sm:inline-flex">
                    NJV TRAVELS
                  </span>
                </div>

                <p className="mt-0.5 text-xs text-[#64748B] sm:text-sm">
                  Tell us about your journey
                </p>
              </div>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="
                flex h-10 w-10 shrink-0 items-center justify-center
                rounded-full border border-[#E2E8E5]
                bg-white text-[#475569]
                transition-all duration-200
                hover:border-[#00A260]/30
                hover:bg-[#00A260]/10
                hover:text-[#00A260]
                focus:outline-none
                focus:ring-4
                focus:ring-[#00A260]/10
              "
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Trust strip */}
          <div className="flex items-center gap-2 border-t border-[#F1F5F3] bg-[#FAFCFB] px-5 py-2.5 sm:px-6">
            <ShieldCheck className="h-3.5 w-3.5 text-[#00A260]" />

            <p className="text-[10px] font-medium text-[#64748B] sm:text-xs">
              Your request is securely submitted to the NJV Travels team.
            </p>
          </div>
        </div>

        {/* Form Content */}
        <div
          className="
            min-h-0 flex-1 overflow-y-auto
            overscroll-contain
            px-4 py-5
            sm:px-6 sm:py-6
          "
        >
          <RideBookingForm onCancel={onClose} />
        </div>
      </div>
    </div>
  );
};

export default RideBookingModal;